import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The bordered list card that the roster, off-site, onboarding and reports
 * lists all share (markup lines 218-240, 244-258, 294-318). The design builds
 * these out of flex rows, not `<table>`, and sizes columns by hand — one fixed
 * 40px avatar cell, a `flex:1` name cell, a fixed 140px status cell and an 18px
 * chevron gutter — so `width` here is a pixel number and an omitted width means
 * "take the remaining space".
 *
 * Separator weight matters: crew tables use `border-top:1.5px solid #EAECF1`
 * (border-inner), while the modal lists use 1px. This is the 1.5px family.
 */
export type DataTableColumn<T> = {
  /** Stable identity for the cell — also the React key. */
  key: string;
  /** Header cell content. Omit for the avatar and chevron gutters. */
  header?: ReactNode;
  /** Fixed pixel width; omit to let the cell flex and truncate. */
  width?: number;
  className?: string;
  render: (row: T) => ReactNode;
};

export type DataTableProps<T> = {
  columns: ReadonlyArray<DataTableColumn<T>>;
  rows: readonly T[];
  getRowKey: (row: T) => string;
  /** Given, rows render as `<button>` and hover to `bg-surface-hover`. */
  onSelect?: (row: T) => void;
  /** Source copy is per-table: "No crew match.", "No on-site periods match." */
  emptyMessage?: ReactNode;
  /** Row-level override — onboarding rows are 13px tall, reports 15px/20px. */
  rowClassName?: string;
  headerClassName?: string;
  className?: string;
};

/** Fixed-width cells never shrink; the flexible one must be able to truncate. */
function cellProps<T>(column: DataTableColumn<T>) {
  if (column.width === undefined) {
    return { className: "min-w-0 flex-1", style: undefined };
  }
  return {
    className: "shrink-0",
    style: { width: column.width },
  };
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onSelect,
  emptyMessage = "No results.",
  rowClassName,
  headerClassName,
  className,
}: DataTableProps<T>) {
  const interactive = onSelect !== undefined;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-card border-[1.5px] border-border bg-surface shadow-card",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-[13px] bg-subtle px-[18px] py-[11px]",
          "font-display text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-muted",
          headerClassName,
        )}
      >
        {columns.map((column) => {
          const cell = cellProps(column);
          return (
            <div
              key={column.key}
              className={cn(cell.className, column.className)}
              style={cell.style}
            >
              {column.header}
            </div>
          );
        })}
      </div>

      {rows.map((row) => {
        const cells = columns.map((column) => {
          const cell = cellProps(column);
          return (
            <div
              key={column.key}
              className={cn(cell.className, column.className)}
              style={cell.style}
            >
              {column.render(row)}
            </div>
          );
        });
        const rowClasses = cn(
          "flex w-full items-center gap-[13px] border-t-[1.5px] border-border-inner",
          "bg-surface px-[18px] py-3 text-left text-ink",
          interactive && "cursor-pointer hover:bg-surface-hover",
          rowClassName,
        );

        if (onSelect !== undefined) {
          // A div with button semantics, NOT a `<button>`: cells may carry
          // their own actions (Offboard, Promote, Delete), and a button inside
          // a button is invalid HTML the browser re-parents — the inner
          // control's clicks then land on the row, or nowhere. React 19 logs
          // the hydration error; the misdelivered clicks were the symptom.
          return (
            <div
              key={getRowKey(row)}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(row)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelect(row);
                }
              }}
              className={rowClasses}
            >
              {cells}
            </div>
          );
        }
        return (
          <div key={getRowKey(row)} className={rowClasses}>
            {cells}
          </div>
        );
      })}

      {rows.length === 0 && (
        <div className="p-[30px] text-center text-[0.88rem] text-muted">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}
